import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import PastOrderService, {
    type PastOrderCommission,
    type PastOrderDetails,
    type PastOrderOutcome,
    type PastOrderRow,
    type PastOrderTimelineEntry
} from '../../services/order/pastOrderService.ts';
import { money } from '../../utils/money.ts';
import { WoltTimeline } from '../../components/wolt/WoltTimeline.tsx';
import { CustomerChips } from '../../components/customer/CustomerChips.tsx';
import { OnSiteBadge, isOnSite, onSiteDoneLabel, orderTypeName } from '../../components/order/DineIn.tsx';

/**
 * One order opened from Order history: its basket, its timeline and, for
 * admins, the fees and who delivered it. Moved from the old Past orders page
 * (23 Oct 2026) when that page and Today's orders became Order history.
 */

const service = new PastOrderService();

/** A UTC instant from the server, in the reader's own clock. */
const when = (iso: string | null | undefined, withSeconds = false) => {
    if (!iso) return '—';
    try {
        return new Intl.DateTimeFormat('en-GB', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: withSeconds ? '2-digit' : undefined, hour12: false,
        }).format(new Date(iso));
    } catch {
        return iso.replace('T', ' ').replace('Z', '');
    }
};

const clock = (iso: string | null | undefined) => {
    if (!iso) return '—';
    try {
        return new Intl.DateTimeFormat('en-GB', {
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        }).format(new Date(iso));
    } catch {
        return iso;
    }
};

const OUTCOME_COLOUR: Record<PastOrderOutcome, 'success' | 'warning' | 'default'> = {
    DELIVERED: 'success',
    REJECTED: 'warning',
    CANCELLED: 'default',
};

// Wolt's blue, as on the courier map, so a Wolt step reads as Wolt at a glance.
const KIND_COLOUR: Record<PastOrderTimelineEntry['kind'], string> = {
    STATUS: 'var(--color-primary, #24bbaa)',
    COURIER: '#7a5af8',
    WOLT: '#009de0',
};

export const OutcomeChip = ({ outcome, orderType }: { outcome: PastOrderOutcome; orderType?: string | null }) => {
    const { t } = useTranslation();
    return (
        <Chip
            size="small"
            color={OUTCOME_COLOUR[outcome]}
            variant={outcome === 'DELIVERED' ? 'filled' : 'outlined'}
            // An on-site order that went through was served (dine in) or collected (pickup), not delivered.
            label={isOnSite(orderType) && outcome === 'DELIVERED' ? onSiteDoneLabel(orderType) : t(`orders.past.outcome.${outcome}`)}
        />
    );
};

export const deliveredByLabel = (row: PastOrderRow, t: (key: string) => string) => {
    if (isOnSite(row.orderType)) return `${orderTypeName(row.orderType)} (no courier)`;
    if (row.deliveredBy === 'WOLT') return 'Wolt';
    if (row.deliveredBy === 'VELLO_COURIER') return row.courierName ?? t('orders.past.ownCourier');
    return '—';
};

/** "Commission 10% (pickup & dine-in rate)"; the rate is left out for old orders without a snapshot. */
const commissionLabel = (c: PastOrderCommission, t: (key: string, fallback: string) => string) => {
    const rate = c.ratePercent != null ? ` ${Number(c.ratePercent)}%` : '';
    const basis = c.basis === 'ON_SITE'
        ? ` (${t('orders.past.commissionOnSite', 'pickup & dine-in rate')})`
        : c.basis === 'DELIVERY' ? ` (${t('orders.past.commissionDelivery', 'delivery rate')})` : '';
    return `${t('orders.past.commission', 'Commission')}${rate}${basis}`;
};

/**
 * Why the commission was not earned, or null when it stands. The snapshot's own
 * flag is the authority (a refund after delivery sets it too); the outcome
 * covers an older backend that does not send it.
 */
export const commissionVoidReason = (
    c: PastOrderCommission,
    outcome: PastOrderOutcome | null | undefined,
    t: (key: string, fallback: string) => string,
): string | null => {
    if (outcome === 'REJECTED') return t('orders.past.commissionVoidRejected', 'cancelled (order rejected)');
    if (outcome === 'CANCELLED') return t('orders.past.commissionVoidCancelled', 'cancelled (order cancelled)');
    if (c.cancelled) return t('orders.past.commissionVoidRefunded', 'cancelled (order refunded)');
    return null;
};

export const PastOrderPanel = ({ orderId, admin, onClose }: { orderId: number; admin: boolean; onClose: () => void }) => {
    const { t } = useTranslation();
    const [details, setDetails] = useState<PastOrderDetails | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setDetails(null);
        setFailed(false);
        service.details(orderId)
            .then((d) => { if (!cancelled) setDetails(d); })
            .catch(() => { if (!cancelled) setFailed(true); });
        return () => { cancelled = true; };
    }, [orderId]);

    const order = details?.order;
    const currency = order?.currency ?? null;

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6">{order?.orderNumber ?? t('orders.past.order')}{order?.dailyNumber ? ` · ${order.dailyNumber}` : ''}</Typography>
                    {order && order.outcome && <OutcomeChip outcome={order.outcome} orderType={order.orderType} />}
                </Stack>
                <IconButton aria-label={t('orders.past.close')} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Stack>

            {failed && <Alert severity="error">{t('orders.past.detailsFailed')}</Alert>}
            {!failed && !details && (
                <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={24} /></Box>
            )}

            {details && order && (
                <Stack spacing={2.5}>
                    {isOnSite(order.orderType) && (
                        <Box><OnSiteBadge orderType={order.orderType} dineIn={order.dineIn} size="large" /></Box>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">{t('orders.past.placed')}</Typography>
                        <Typography variant="body2">{when(order.placedAt)}</Typography>
                        {admin && (
                            <>
                                <Typography variant="body2" color="text.secondary">{t('orders.past.columns.restaurant')}</Typography>
                                <Typography variant="body2">{order.restaurantName}</Typography>
                            </>
                        )}
                        <Typography variant="body2" color="text.secondary">{t('orders.past.columns.customer')}</Typography>
                        <Box>
                            <Typography variant="body2">{order.customerName ?? '—'}</Typography>
                            <CustomerChips prev={order.prev} src={order.src} unit="order" />
                        </Box>
                        <Typography variant="body2" color="text.secondary">{t('orders.paymentType')}</Typography>
                        <Typography variant="body2">{t(`orders.past.payment.${order.paymentType}`)}</Typography>
                        {admin && (
                            <>
                                <Typography variant="body2" color="text.secondary">{t('orders.past.columns.deliveredBy')}</Typography>
                                <Typography variant="body2">
                                    {deliveredByLabel(order, t)}
                                    {details.courierPhone ? ` · ${details.courierPhone}` : ''}
                                </Typography>
                            </>
                        )}
                        {admin && details.woltPrice && (
                            <>
                                <Typography variant="body2" color="text.secondary">{t('orders.past.woltCharge')}</Typography>
                                <Typography variant="body2">{money(details.woltPrice.amount, details.woltPrice.currency)}</Typography>
                            </>
                        )}
                    </Box>

                    {details.notes && (
                        <Alert severity="info" icon={false}>
                            <Typography variant="body2"><strong>{t('orders.notes')}:</strong> {details.notes}</Typography>
                        </Alert>
                    )}

                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('orders.past.basket')}</Typography>
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small" sx={{ '& td': { fontVariantNumeric: 'tabular-nums', verticalAlign: 'top' } }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('orders.past.item')}</TableCell>
                                        <TableCell align="right">{t('orders.past.unitPrice')}</TableCell>
                                        <TableCell align="right">{t('orders.past.lineTotal')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {details.lines.map((line, i) => (
                                        <TableRow key={i}>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {line.quantity} × {line.name ?? '—'}
                                                </Typography>
                                                {line.options.map((option, j) => (
                                                    <Typography key={j} variant="caption" color="text.secondary" component="div">
                                                        {option.category ? `${option.category}: ` : ''}{option.name}
                                                        {option.price && Number(option.price) > 0 ? ` (+${money(option.price, currency)})` : ''}
                                                    </Typography>
                                                ))}
                                            </TableCell>
                                            <TableCell align="right">{money(line.unitPrice, currency)}</TableCell>
                                            <TableCell align="right">{money(line.lineTotal, currency)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {details.lines.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} sx={{ color: 'text.secondary' }}>{t('orders.past.noLines')}</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>

                        <Box
                            sx={{
                                mt: 1.5,
                                display: 'grid',
                                gridTemplateColumns: '1fr auto',
                                rowGap: 0.5,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {admin ? (
                                <>
                                    <Typography variant="body2" color="text.secondary">{t('orders.itemSubtotal')}</Typography>
                                    <Typography variant="body2" align="right">{money(order.itemSubTotal, currency)}</Typography>
                                    <Typography variant="body2" color="text.secondary">{t('orders.delivery')}</Typography>
                                    <Typography variant="body2" align="right">{money(order.deliveryFee, currency)}</Typography>
                                    <Typography variant="body2" color="text.secondary">{t('orders.serviceFee')}</Typography>
                                    <Typography variant="body2" align="right">{money(order.serviceFee, currency)}</Typography>
                                    {order.creditRedeemed !== null && Number(order.creditRedeemed) > 0 && (
                                        <>
                                            <Typography variant="body2" color="text.secondary">{t('orders.past.creditRedeemed')}</Typography>
                                            <Typography variant="body2" align="right">−{money(order.creditRedeemed, currency)}</Typography>
                                        </>
                                    )}
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('orders.total')}</Typography>
                                    <Typography variant="body2" align="right" sx={{ fontWeight: 700 }}>{money(order.totalPrice, currency)}</Typography>
                                    {/* Vello's commission (admins only; the server sends none to a restaurant). */}
                                    {details.commission && (() => {
                                        // A rejected/cancelled/refunded order earns Vello nothing: say so, and strike the amount.
                                        const voided = commissionVoidReason(details.commission, order.outcome, t);
                                        return (
                                            <>
                                                <Typography variant="body2" color="text.secondary" data-testid="commission-label">
                                                    {commissionLabel(details.commission, t)}
                                                    {voided && (
                                                        <Box component="span" sx={{ color: 'warning.main', fontWeight: 600 }}>{` · ${voided}`}</Box>
                                                    )}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    align="right"
                                                    color={voided ? 'text.disabled' : 'text.secondary'}
                                                    sx={voided ? { textDecoration: 'line-through' } : undefined}
                                                    data-testid="commission-amount"
                                                >
                                                    {details.commission.amount != null ? money(details.commission.amount, currency) : '—'}
                                                </Typography>
                                            </>
                                        );
                                    })()}
                                </>
                            ) : (
                                <>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{t('orders.foodTotal', 'Food total')}</Typography>
                                    <Typography variant="body2" align="right" sx={{ fontWeight: 700 }}>{money(order.itemSubTotal, currency)}</Typography>
                                </>
                            )}
                        </Box>
                    </Box>

                    <Divider />

                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('orders.past.ledger')}</Typography>
                        <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                            {details.timeline.map((entry, i) => (
                                <Box
                                    component="li"
                                    key={i}
                                    sx={{ display: 'grid', gridTemplateColumns: '72px 12px 1fr', columnGap: 1, py: 0.5 }}
                                >
                                    <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', pt: '2px' }}>
                                        {clock(entry.at)}
                                    </Typography>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: KIND_COLOUR[entry.kind], mt: '6px' }} />
                                    <Box>
                                        <Typography variant="body2">{entry.label}</Typography>
                                        {entry.detail && (
                                            <Typography variant="caption" color="text.secondary" component="div">
                                                {entry.detail}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {admin && details.woltDelivery && (
                        <>
                            <Divider />
                            <WoltTimeline delivery={details.woltDelivery} historical />
                        </>
                    )}
                </Stack>
            )}
        </Box>
    );
};

